/**
 * Tenant brand (HANDOFF §1.3). Pure colour maths in `computeBrand` (usable server side when the tenant saves
 * its settings); `applyTenantBrand` writes the result to <html> for the running app.
 *
 * - onBrand   = #ffffff or #141c26, whichever has the higher WCAG contrast against the primary colour.
 * - brandText = the primary colour darkened in OKLab lightness steps until contrast with #ffffff is ≥ 4.5:1
 *               (light theme). brandTextDark is the same idea against the dark surface (#141c25), lightened.
 */
export interface BrandColors {
  primary: string;
  onBrand: "#ffffff" | "#141c26";
  /** Link / selected-nav colour on light surfaces (≥ 4.5:1 on white). */
  brandText: string;
  /** Link / selected-nav colour on dark surfaces (≥ 4.5:1 on #141c25). */
  brandTextDark: string;
  /** Contrast of onBrand against the primary colour (buttons). */
  onBrandContrast: number;
}

const INK = "#141c26";
const DARK_SURFACE = "#141c25";
const DEFAULT_BRAND = "#1f4e79";

type Rgb = [number, number, number]; // 0..1 linear or sRGB depending on context

export function parseHex(hex: string): Rgb | null {
  const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  let h = m[1]!;
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  const n = parseInt(h, 16);
  return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];
}

export function toHex([r, g, b]: Rgb): string {
  const c = (v: number) => Math.round(Math.min(1, Math.max(0, v)) * 255).toString(16).padStart(2, "0");
  return `#${c(r)}${c(g)}${c(b)}`;
}

const toLinear = (c: number) => (c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
const toGamma = (c: number) => (c <= 0.0031308 ? 12.92 * c : 1.055 * c ** (1 / 2.4) - 0.055);

function luminance(rgb: Rgb): number {
  const [r, g, b] = rgb.map(toLinear) as Rgb;
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** WCAG 2.x contrast ratio between two hex colours (1..21). */
export function contrastRatio(a: string, b: string): number {
  const ra = parseHex(a), rb = parseHex(b);
  if (!ra || !rb) return 1;
  const la = luminance(ra), lb = luminance(rb);
  const [hi, lo] = la > lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
}

// OKLab (Björn Ottosson). Input/output sRGB 0..1 (gamma encoded).
export function rgbToOklab(rgb: Rgb): [number, number, number] {
  const [r, g, b] = rgb.map(toLinear) as Rgb;
  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
  const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);
  return [
    0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s,
    1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s,
    0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s,
  ];
}

export function oklabToRgb([L, a, b]: [number, number, number]): Rgb {
  const l = (L + 0.3963377774 * a + 0.2158037573 * b) ** 3;
  const m = (L - 0.1055613458 * a - 0.0638541728 * b) ** 3;
  const s = (L - 0.0894841775 * a - 1.291485548 * b) ** 3;
  const r = 4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s;
  const g = -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s;
  const bb = -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s;
  return [r, g, bb].map((c) => Math.min(1, Math.max(0, toGamma(Math.max(0, c))))) as Rgb;
}

/** Moves OKLab lightness in `step`s until the colour reaches `target` contrast against `against` (max 60 steps). */
function adjustUntilContrast(hex: string, against: string, step: number, target = 4.5): string {
  const rgb = parseHex(hex);
  if (!rgb) return hex;
  let lab = rgbToOklab(rgb);
  let out = toHex(rgb);
  for (let i = 0; i < 60 && contrastRatio(out, against) < target; i++) {
    lab = [Math.min(1, Math.max(0, lab[0] + step)), lab[1], lab[2]];
    out = toHex(oklabToRgb(lab));
    if (lab[0] <= 0 || lab[0] >= 1) break;
  }
  return out;
}

/** Pure: derives the brand palette from the tenant's primary colour. Invalid input falls back to the product blue. */
export function computeBrand(primaryColor: string | null | undefined): BrandColors {
  const primary = primaryColor && parseHex(primaryColor) ? toHex(parseHex(primaryColor)!) : DEFAULT_BRAND;
  const cWhite = contrastRatio(primary, "#ffffff");
  const cInk = contrastRatio(primary, INK);
  const onBrand = cWhite >= cInk ? "#ffffff" : INK;
  return {
    primary,
    onBrand,
    onBrandContrast: Math.max(cWhite, cInk),
    brandText: adjustUntilContrast(primary, "#ffffff", -0.02),
    brandTextDark: adjustUntilContrast(primary, DARK_SURFACE, +0.02),
  };
}

export interface TenantBrand { primaryColor?: string | null; logoUrl?: string | null }

let currentBrand: (BrandColors & { logoUrl: string | null }) | null = null;

/**
 * Applies a tenant's brand to <html>: `--tenant-brand`, `--on-brand`, `--brand-text-light`, `--brand-text-dark`
 * (styles.css picks the right one per theme) and `--tenant-logo`. Call with `{}` to reset to the product default.
 */
export function applyTenantBrand(brand: TenantBrand): BrandColors {
  const colors = computeBrand(brand.primaryColor);
  if (typeof document !== "undefined") {
    const s = document.documentElement.style;
    s.setProperty("--tenant-brand", colors.primary);
    s.setProperty("--on-brand", colors.onBrand);
    s.setProperty("--brand-text-light", colors.brandText);
    s.setProperty("--brand-text-dark", colors.brandTextDark);
    if (brand.logoUrl) s.setProperty("--tenant-logo", `url("${brand.logoUrl}")`); else s.removeProperty("--tenant-logo");
  }
  currentBrand = { ...colors, logoUrl: brand.logoUrl ?? null };
  return colors;
}

/** The brand last applied with `applyTenantBrand` (null before the first call). */
export function getTenantBrand() { return currentBrand; }
